import { AfterViewInit, Component, HostListener, OnInit, OnDestroy, QueryList, ViewChildren } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, Subscription, filter, of, startWith, switchMap, take } from 'rxjs';
import { UserModel } from '../../../../account/core/models/user.model';
import { AuthService } from 'src/app/account/core/services/auth.service';
import KTLayoutHeaderTopbar from '../../../../../assets/js/layout/base/header-topbar';
import { KTUtil } from '../../../../../assets/js/components/util';
import { ImpersonationService } from '../../../../account/core/services/impersonation.service';
import { ImpersonationResponseModel } from '../../../../account/core/models/impersonation.model';
import { environment } from 'src/environments/environment';
import { LiveViewService } from 'src/app/main/core/services/live-view.service';
import { IdleService } from 'src/app/_shared/core/services/idle.service';
import { NavigationEnd, ResolveEnd, Router } from '@angular/router';
import { PageHeaderService } from '../../core/services/page-header.service';
import { NgbDropdown, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ChangePasswordComponent } from 'src/app/account/change-password/change-password.component';
import { ToastService } from 'src/app/_shared/core/services/toast.service';
import { ExportLookUp, ExportIncidentService } from 'src/app/account/core/services/export-incident.service';
import { LoggerCommonService } from 'src/app/_shared/core/services/logger-common.service';
import { OPERATIONS, PAGE } from 'src/app/_shared/core/constants/app-insight-constants';
import { LinkedUserAuthModel, LinkedUserResponseModel } from 'src/app/account/core/models/auth.model';
import { LoaderService } from '../../core/services/loader.service';
import { UserSettingService } from 'src/app/main/core/services/user-setting.service';
import { UserSettings } from 'src/app/main/core/models/user-setting.model';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { CookieService } from 'ngx-cookie-service';
import { PermissionService } from 'src/app/_shared/core/services/permission.service';
import { ExportIncidentReportsService } from 'src/app/account/core/services/export-incident-reports.service';
import { BRANDING } from 'src/app/_shared/core/constants/branding.constants';
import { SessionManagerService } from 'src/app/_shared/core/services/session-manager-service';
import { ExportMonthlyDashboardService } from 'src/app/account/core/services/export-monthly-dashboard.service';
import { ExportAssetDashboardService } from 'src/app/account/core/services/export-asset-dashboard.service';

const CMS_BASE_URL = environment.cmsAppUrl;

@Component({
  selector: 'sc-topbar',
  templateUrl: './topbar.component.html',
  styleUrls: ['./topbar.component.scss']
})
export class TopbarComponent implements OnInit, OnDestroy, AfterViewInit {

  // Observables
  title$: Observable<string>;
  icon$: Observable<string>;

  user$: Observable<UserModel>;
  isLoading$: Observable<boolean>;

  isExporting$: Observable<boolean>;
  isExportingCSV$: Observable<boolean>;
  isExportingPDF$: Observable<boolean>;
  isExportingExcel$: Observable<boolean>;

  isExportingIncidentReport$: Observable<boolean>;
  isExportingIncidentReportCSV$: Observable<boolean>;
  isExportingIncidentReportPDF$: Observable<boolean>;
  isExportingIncidentReportExcel$: Observable<boolean>;

  isExportingMonthlyDashboard$: Observable<boolean>;
  isExportingMonthlyDashboardCSV$: Observable<boolean>;
  isExportingMonthlyDashboardPDF$: Observable<boolean>;
  isExportingMonthlyDashboardExcel$: Observable<boolean>;

  isExportingAssetDashboard$: Observable<boolean>;
  isExportingAssetDashboardCSV$: Observable<boolean>;
  isExportingAssetDashboardExcel$: Observable<boolean>;

  loggingOut$: Observable<boolean>;
  private readonly loggingOutSubject: BehaviorSubject<boolean>;

  // Linked Users and Tenants
  linkedUser$: Observable<LinkedUserResponseModel[]>;
  linkedUser: LinkedUserResponseModel[];
  currentUser: number;
  public selected: LinkedUserResponseModel;
  public filtered$: Observable<LinkedUserResponseModel[]>;
  public linkedTenantForm: UntypedFormGroup;

  // Loading and State Management
  switchTenantLoadingMap: { [key: string]: boolean } = {};
  isLinkedTenantLoading: boolean;
  isFilterApplied: boolean = false;
  hasDashboardPermission: boolean = true;
  isLinkedUsers: boolean = false;
  collapsed: boolean = true;

  // User Settings
  existingSettings: UserSettings;
  private readonly unsubscribe: Subscription[] = [];

  // Export Icons
  requireExportIconsPage: string = '';

  // Do not display export PDF Icon for Asset Dashboard and Change Dashboard
  get requireExportPdfIconsPage(): boolean {
    return this.requireExportIconsPage !== PAGE.AssetDashboard && 
          this.requireExportIconsPage !== PAGE.ChangeDashboard;
  }
  
  // Event Listener for Browser Close
  @HostListener("window:beforeunload", ["$event"])
  onBeforeUnload(event: Event) {
    this.loggingOutSubject.next(true);
    IdleService.runInitialTimer = false;
    IdleService.runFinalTimer = false;
    this.liveViewService.disconnectStreamsByTenant(this.authenticationService.currentTenantIdValue);
  }

  constructor(
    private readonly pageHeaderService: PageHeaderService,
    private readonly authService: AuthService,
    private readonly liveViewService: LiveViewService,
    private readonly authenticationService: AuthService,
    private readonly impersonationService: ImpersonationService,
    private readonly router: Router,
    private readonly modalService: NgbModal,
    private readonly toastService: ToastService,
    private readonly exportIncidentService: ExportIncidentService,
    private readonly exportIncidentReportsService: ExportIncidentReportsService,
    private readonly logger: LoggerCommonService,
    private readonly loaderService: LoaderService,
    private readonly userSettingService: UserSettingService,
    private readonly cookieService: CookieService,
    private readonly permissionService: PermissionService,
    private readonly sessionService: SessionManagerService,
    private readonly exportMonthlyDashboardService: ExportMonthlyDashboardService,
    private readonly exportAssetDashboardService: ExportAssetDashboardService,
  ) {
    this.title$ = this.pageHeaderService.currentTitle$;
    this.icon$ = this.pageHeaderService.currentIcon$;

    this.user$ = this.authService.currentUser$;
    this.isLoading$ = this.impersonationService.isLoading$;
    
    this.isExporting$ = this.exportIncidentService.isExporting$;
    this.isExportingCSV$ = this.exportIncidentService.isExportingCSV$;
    this.isExportingPDF$ = this.exportIncidentService.isExportingPDF$;
    this.isExportingExcel$ = this.exportIncidentService.isExportingExcel$;
    
    this.linkedUser = this.authService.currentListOfTenantsValue;
   
    this.isExportingIncidentReport$ = this.exportIncidentReportsService.isExporting$;
    this.isExportingIncidentReportCSV$ = this.exportIncidentReportsService.isExportingCSV$;
    this.isExportingIncidentReportPDF$ = this.exportIncidentReportsService.isExportingPDF$;
    this.isExportingIncidentReportExcel$ = this.exportIncidentReportsService.isExportingExcel$;

    this.isExportingMonthlyDashboard$ = this.exportMonthlyDashboardService.isExporting$;
    this.isExportingMonthlyDashboardCSV$ = this.exportMonthlyDashboardService.isExportingCSV$;
    this.isExportingMonthlyDashboardExcel$ = this.exportMonthlyDashboardService.isExportingExcel$;

    this.isExportingAssetDashboard$ = this.exportAssetDashboardService.isExporting$;
    this.isExportingAssetDashboardCSV$ = this.exportAssetDashboardService.isExportingCSV$;
    this.isExportingAssetDashboardExcel$ = this.exportAssetDashboardService.isExportingExcel$;

    this.loggingOutSubject = new BehaviorSubject<boolean>(false);
    this.loggingOut$ = this.loggingOutSubject.asObservable();

    this.initPageHeader();
  }

  @ViewChildren(NgbDropdown)
  dropdowns: QueryList<NgbDropdown>
  private dropdown: NgbDropdown;

  ngOnInit(): void {
    this.linkedTenantForm = new UntypedFormGroup({
      userId: new UntypedFormControl('', Validators.required)
    });
    this.toggleIncidentExportIcons();
    this.filtered$ = of([]);
    this.selected = new LinkedUserResponseModel();
    this.updateFormSearch()
    
    this.getLinkedUserStatus();

    // filter data to exclude the current login user from them
    this.user$.subscribe(cUser => {
      this.currentUser = cUser.id;
      this.hasDashboardPermission = this.permissionService.hasPermission('Pages.Tenant.Dashboard');
    });
  }

  initPageHeader() {
    const initPageHeader = () => {
      setTimeout(() => {
        this.pageHeaderService.updateAfterRouteChanges(this.router.url);
      }, 0);
    };

    initPageHeader();
    // subscribe to router events
    this.router.events
      .pipe(filter((event) => event instanceof ResolveEnd))
      .subscribe(initPageHeader);

    this.router.events.subscribe((val) => {
      if (val instanceof NavigationEnd) {
        this.toggleIncidentExportIcons();
      }
    });
  }

  ngAfterViewInit(): void {
    KTUtil.ready(() => {
      // Init Header Topbar For Mobile Mode
      KTLayoutHeaderTopbar.init('kt_header_mobile_topbar_toggle');
    });
  }

  triggerExport(exportType: ExportLookUp, page: string): void {
    this.logger.trackClick(ExportLookUp[exportType]);
    if (page == PAGE.Incidents)
      this.exportIncidentService.exportObservable(exportType);
    else if (page == PAGE.IncidentsReports)
      this.exportIncidentReportsService.exportObservable(exportType);
    else if (page == PAGE.ChangeDashboard)
      this.exportMonthlyDashboardService.exportObservable(exportType);
    else if (page == PAGE.AssetDashboard)
      this.exportAssetDashboardService.exportObservable(exportType);
  }

  unimpersonate() {
    this.logger.trackClick(OPERATIONS.Unimpersonate);
    this.loggingOutSubject.next(true);
    IdleService.runInitialTimer = false;
    IdleService.runFinalTimer = false;

    this.liveViewService.disconnectStreamsByTenant(this.authenticationService.currentTenantIdValue);

    setTimeout(() => {
      this.impersonationService.unimpersonate().subscribe({
        next: (response: ImpersonationResponseModel) => {
          if (!response?.impersonationToken) {
            this.logger.error('Failed to unimpersonate');
            throw new Error('Failed to unimpersonate');
          }

          location.href = `${CMS_BASE_URL}?impersonationToken=${response.impersonationToken}`;
        },
        error: (error: any) => {
          throw error;
        }
      });
    }, 5000)

  }

  logout() {
    this.logger.trace(PAGE.LogOut, OPERATIONS.LoggedOut, this.authenticationService?.currentUserValue?.name)
    this.logger.trackClick(OPERATIONS.LoggedOut)
    this.loggingOutSubject.next(true);
    IdleService.runInitialTimer = false;
    IdleService.runFinalTimer = false;
    this.liveViewService.disconnectStreamsByTenant(this.authenticationService.currentTenantIdValue);
    this.authService.logout();
    this.sessionService.clearSession();
    // Navigating using Angular router instead of document.location.reload()
    // to ensure the logout API call completes properly
    this.router.navigate(['/account', 'login']);
  }

  switchTenant(userId: any, tenantId: any) {
    this.switchTenantLoadingMap[userId] = true;
    this.loaderService.isLoadingSubject.next(true);
    this.userSettingService.removeUserSettingsFromStore();
    const userAuth = new LinkedUserAuthModel();
    userAuth.targetTenantId = tenantId;
    userAuth.partnerId = Number.parseInt(BRANDING.PARTNER_ID);

    const switchTenant = this.authService.loginAsTenantUser(userAuth).subscribe({
      next: (response) => {    
        this.switchTenantLoadingMap[userId] = false;
        const defaultSelection = new UserSettings();
        defaultSelection.isLinkedUserSwitched = true
        defaultSelection.cameraType = "";
        this.userSettingService.userSettingValue = defaultSelection;
        document.location.href = '/';
      }, error: (error: any) => {
        this.switchTenantLoadingMap[userId] = false;
        this.linkedUser = this.linkedUser.filter(user => user.userId !== userId);
        document.location.href = '/';
        throw error;
      }
    });

    this.unsubscribe.push(switchTenant);
  }

  getLinkedUserStatus() {
    const hasLinkedCookie = this.cookieService.check(environment.linkedUserKey);
    const isSsoUser = this.cookieService.check(environment.isSsoOnlyUser);
    
    this.isLinkedUsers = hasLinkedCookie || isSsoUser;
  }

  getLinkedTenantsList() {
    this.getLinkedUserStatus();

    if (!this.isLinkedUsers) {
      return; 
    }

    this.user$.pipe(
      take(1),
      switchMap(user => {
        if (user.isImpersonated) {
          this.cookieService.delete(environment.linkedUserKey, '/');
          return EMPTY;
        } else {
          return this.authService.getLinkedUser(Number.parseInt(BRANDING.PARTNER_ID),true);
        }
      })
    ).subscribe({
      next: (linkedUsers: LinkedUserResponseModel[]) => {
        this.authService.setListOfLinkedUserInCookie([]);
        this.linkedUser = linkedUsers;
      }
    });
    // Reset the search field every time dropdown is opened
    this.linkedTenantForm.get('userId')?.setValue('');
    this.isFilterApplied = false;
    this.filtered$ = of([]);
  }

  changePassword(dropDown: any) {
    this.logger.trackClick('ChangePassword');

    this.dropdown = this.dropdowns.find(x => (x as any)?._elementRef?.nativeElement === dropDown);

    if (this.dropdown) {
      this.dropdown.close();
    }

    const changePasswordModal = this.modalService.open(ChangePasswordComponent, { 
      size: 'md', 
      centered: true, 
      backdrop: 'static', 
      keyboard: false 
    });

    changePasswordModal.result.then(
      () => {
        this.toastService.success(`Password changed successfully.`);
      },
      (reason) => {
        if (reason && reason instanceof Error) {
          this.logger.error(reason);
        } else {
          // Nothing here.
        }
      }
    );
  }

  toggleIncidentExportIcons(): void {
    if (this.router.url.includes("/main/incidents")) {
      this.requireExportIconsPage = PAGE.Incidents
    } else if (this.router.url.includes("/main/incident-reports")) {
      this.requireExportIconsPage = PAGE.IncidentsReports
    } else if (this.router.url.includes("/main/monthly-dashboard")) {
      this.requireExportIconsPage = PAGE.ChangeDashboard
    } else if (this.router.url.includes("/main/asset-dashboard")) {
      this.requireExportIconsPage = PAGE.AssetDashboard
    } else {
      this.requireExportIconsPage = '';
    }
  }

  updateFormSearch() {
    const tenantListControl = this.linkedTenantForm.get('userId');
    tenantListControl.valueChanges.pipe(
      startWith(''),
    ).subscribe((value: string) => {
      this.isFilterApplied = !!value; 
      this.filtered$ = of(this.filter(this.linkedUser, value));
    });

  }

  private filter(linkedUser: LinkedUserResponseModel[], value: any): LinkedUserResponseModel[] {
    if (value) {
      const filterValue = value.toLowerCase();
      return this.linkedUser.filter(user => {
        const tenantName = user.tenantName ? user.tenantName.toLowerCase() : '';
        const tenancyName = user.tenancyName ? user.tenancyName.toString().toLowerCase() : '';
        return ((tenantName.includes(filterValue) || tenancyName.includes(filterValue)) && user.userId !== this.currentUser);
      });
    }
    return linkedUser;
  }

  ngOnDestroy(): void {
    if (this.modalService.hasOpenModals()) {
      this.modalService.dismissAll();
    }

    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }
}
